import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setUnauthorizedHandler } from "../../core/api/client";
import { signOutThunk } from "../../features/auth/store/authSlice";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createTransform,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { clearAuthSession, setAuthSession } from "../../core/auth/authSession";
import adminReducer from "../../features/admin/store/adminSlice";
import authReducer, { initialAuthState } from "../../features/auth/store/authSlice";
import billingReducer, { billingInitialState } from "../../features/billing/store/billingSlice";
import logsReducer, { logsInitialState } from "../../features/logs/store/logsSlice";
import numbersReducer from "../../features/numbers/store/numbersSlice";
import notificationsReducer from "../../features/notifications/store/notificationsSlice";
import pbxConfigReducer, {
  pbxConfigInitialState,
} from "../../features/pbx-config/store/pbxConfigSlice";
import referralReducer, { referralInitialState } from "../../features/referral/store/referralSlice";
import supportReducer, {
  supportInitialState,
} from "../../features/support/store/supportSlice";
import usersReducer from "../../features/users/store/usersSlice";

// Persist 
const authPersistTransform = createTransform(
  (inboundState) => ({
    twoFaChallenge: {
      token: null,
      email: "",
      user: null,
    },
    session: {
      token: inboundState?.session?.token || null,
      user: inboundState?.session?.user || null,
      isAuthenticated: Boolean(inboundState?.session?.token),
    },
    loginState: {
      state:
        inboundState?.loginState?.state === "two_fa_login"
          ? "login"
          : inboundState?.loginState?.state || "login",
      message: null,
    },
  }),
  (outboundState) => ({
    ...initialAuthState,
    session: {
      token: outboundState?.session?.token || null,
      user: outboundState?.session?.user || null,
      isAuthenticated: Boolean(outboundState?.session?.token),
    },
    twoFaChallenge: {
      token: null,
      email: "",
      user: null,
    },
    loginState: {
      state: outboundState?.loginState?.state || "login",
      message: null,
    },
    isLoading: true,
    isActionLoading: false,
  }),
  { whitelist: ["auth"] },
);

const logsPersistTransform = createTransform(
  (inboundState) => ({
    spreadTable: Boolean(inboundState?.spreadTable),
  }),
  (outboundState) => ({
    ...logsInitialState,
    spreadTable: false,
  }),
  { whitelist: ["logs"] },
);


const referralPersistTransform = createTransform(
  (inboundState) => ({
    currentPage: Number.isInteger(inboundState?.currentPage)
      ? inboundState.currentPage
      : 0,
  }),
  (outboundState) => ({
    ...referralInitialState,
    currentPage: Number.isInteger(outboundState?.currentPage)
      ? outboundState.currentPage
      : 0,
    hasLoaded: false,
  }),
  { whitelist: ["referral"] },
);

const billingPersistTransform = createTransform(
  (inboundState) => ({
    currentPage: Number.isInteger(inboundState?.currentPage)
      ? inboundState.currentPage
      : 0,
  }),
  (outboundState) => ({
    ...billingInitialState,
    currentPage: Number.isInteger(outboundState?.currentPage)
      ? outboundState.currentPage
      : 0,
    hasLoaded: false,
  }),
  { whitelist: ["billing"] },
);


const SYSTEM_STATUS_VALUES = [
  "Operational",
  "Partial Issues",
  "Experiencing Delays",
  "Down",
];

const sanitizeSystemStatusServices = (services) => {
  if (!Array.isArray(services)) {
    return [];
  }

  return services
    .map((item) => {
      const status = SYSTEM_STATUS_VALUES.includes(item?.status)
        ? item.status
        : "-";

      return {
        key: String(item?.key || ""),
        serviceName: String(item?.serviceName || ""),
        endpoint: String(item?.endpoint || ""),
        lastUpdated: String(item?.lastUpdated || ""),
        status,
        notes: String(item?.notes || ""),
      };
    })
    .filter((item) => item.key && item.serviceName);
};

const getSystemStatusCounts = (services) => {
  const counts = {
    Operational: 0,
    "Partial Issues": 0,
    "Experiencing Delays": 0,
    Down: 0,
  };

  services.forEach((service) => {
    if (counts[service.status] !== undefined) {
      counts[service.status] += 1;
    }
  });

  return counts;
};

const supportPersistTransform = createTransform(
  (inboundState) => ({
    systemStatusServices: sanitizeSystemStatusServices(
      inboundState?.systemStatusServices,
    ),
    systemStatusHasLoaded: Boolean(inboundState?.systemStatusHasLoaded),
  }),
  (outboundState) => {
    const services = sanitizeSystemStatusServices(
      outboundState?.systemStatusServices,
    );

    return {
      ...supportInitialState,
      systemStatusServices: services,
      systemStatusCounts: getSystemStatusCounts(services),
      systemStatusLoading: false,
      systemStatusError: "",
      systemStatusHasLoaded:
        Boolean(outboundState?.systemStatusHasLoaded) || services.length > 0,
    };
  },
  { whitelist: ["support"] },
);

const sanitizePbxWhitelistedRecords = (records) => {
  if (!Array.isArray(records)) {
    return [];
  }

  return records
    .map((item) => ({
      id: Number(item?.id || 0) || null,
      client_id: Number(item?.client_id || 0) || null,
      ip_address: String(item?.ip_address || "").trim(),
      created_at: String(item?.created_at || ""),
      updated_at: String(item?.updated_at || ""),
    }))
    .filter((item) => item.ip_address)
    .filter(
      (item, index, list) =>
        list.findIndex((entry) => entry.ip_address === item.ip_address) === index,
    );
};

const pbxConfigPersistTransform = createTransform(
  (inboundState) => ({
    clientId: Number(inboundState?.clientId || 0) || null,
    callerIdTag: String(inboundState?.callerIdTag || ""),
    persistedCallerIdTag: String(inboundState?.persistedCallerIdTag || ""),
    enforceTLS: Boolean(inboundState?.enforceTLS),
    persistedEnforceTLS: Boolean(inboundState?.persistedEnforceTLS),
    ipWhitelistRecords: sanitizePbxWhitelistedRecords(
      inboundState?.ipWhitelistRecords,
    ),
    persistedIpRecords: sanitizePbxWhitelistedRecords(
      inboundState?.persis,
    ),
    hasLoaded: Boolean(inboundState?.hasLoaded),
  }),
  (outboundState) => {
    const ipWhitelistRecords = sanitizePbxWhitelistedRecords(
      outboundState?.ipWhitelistRecords,
    );
    const persistedIpRecords = sanitizePbxWhitelistedRecords(
      outboundState?.persistedIpRecords,
    );

    return {
      ...pbxConfigInitialState,
      clientId: Number(outboundState?.clientId || 0) || null,
      callerIdTag: String(outboundState?.callerIdTag || ""),
      persistedCallerIdTag: String(outboundState?.persistedCallerIdTag || ""),
      enforceTLS: Boolean(outboundState?.enforceTLS),
      persistedEnforceTLS: Boolean(outboundState?.persistedEnforceTLS),
      ipWhitelistRecords,
      persistedIpRecords,
      hasLoaded:
        Boolean(outboundState?.hasLoaded) || ipWhitelistRecords.length > 0,
      isLoading: false,
      isSaving: false,
      error: "",
      newIpAddress: "",
      leftEmail: "",
      rightEmail: "",
    };
  },
  { whitelist: ["pbxConfig"] },
);

const reducers = {
  admin: adminReducer,
  auth: authReducer,
  billing: billingReducer,
  logs: logsReducer,
  numbers: numbersReducer,
  notifications: notificationsReducer,
  pbxConfig: pbxConfigReducer,
  referral: referralReducer,
  support: supportReducer,
  users: usersReducer,
};

const reducerKeys = Object.keys(reducers);
const persistedReducerKeys = [
  "auth",
  "logs",
  "referral",
  "billing",
  "support",
  "pbxConfig",
];

const stripUnexpectedReducerState = (state) => {
  if (!state || typeof state !== "object" || Array.isArray(state)) {
    return state;
  }

  let hasUnexpectedKey = false;
  const sanitizedState = {};

  Object.keys(state).forEach((key) => {
    if (reducerKeys.includes(key)) {
      sanitizedState[key] = state[key];
    } else {
      hasUnexpectedKey = true;
    }
  });

  return hasUnexpectedKey ? sanitizedState : state;
};

const appReducer = combineReducers(reducers);

const rootReducer = (state, action) => {
  // On sign-out, drop every feature slice back to its initial state so the next
  // organization's data isn't served stale from the previous session's cache.
  // Auth is preserved here because it owns the sign-out transition itself
  // (clearing the session and resetting loginState without re-triggering the
  // initial loading gate).
  if (action.type === signOutThunk.fulfilled.type) {
    return appReducer({ auth: state?.auth }, action);
  }

  return appReducer(stripUnexpectedReducerState(state), action);
};

const migratePersistedState = (state) =>
  Promise.resolve(stripUnexpectedReducerState(state));

// Root persist configuration.
const persistConfig = {
  key: "root",
  version: 2,
  storage,
  whitelist: persistedReducerKeys,
  migrate: migratePersistedState,
  transforms: [
    authPersistTransform,
    logsPersistTransform,
    referralPersistTransform,
    billingPersistTransform,
    supportPersistTransform,
    pbxConfigPersistTransform,
  ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        ignoredActionPaths: ["meta.arg.attachment"],
      },
    }),
});

let previousToken;
let previousUser;

store.subscribe(() => {
  const session = store.getState()?.auth?.session;
  const nextToken = session?.token || null;
  const nextUser = session?.user || null;

  if (nextToken === previousToken && nextUser === previousUser) {
    return;
  }

  previousToken = nextToken;
  previousUser = nextUser;

  if (nextToken) {
    setAuthSession({ token: nextToken, user: nextUser });
  } else {
    clearAuthSession();
  }
});

// Wire up global 401 handler: any authenticated request that gets a 401 signs the user out.
setUnauthorizedHandler(() => store.dispatch(signOutThunk()));

// persistor controls rehydration and explicit purge/flush flows.
export const persistor = persistStore(store);
